import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { 
    getAuth, 
    GoogleAuthProvider,
    signInWithEmailLink,
    sendSignInLinkToEmail,
    connectAuthEmulator,
    setPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import { getFirestore, doc, getDoc, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// Initialize Auth FIRST
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
const db = getFirestore(app);
const functions = getFunctions(app);
const storage = getStorage(app);

// Determine the emulator host - use IP address if accessing from network, otherwise localhost
// This allows mobile devices to connect to emulators running on your computer
const getEmulatorHost = () => {
    // Detect if accessing via IP address (mobile/network) or localhost (desktop)
    const hostname = window.location.hostname;
    
    // If hostname is an IP address (matches pattern like 192.168.x.x or 10.x.x.x)
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (ipPattern.test(hostname) && hostname !== '127.0.0.1') {
        // Accessing from network (mobile device) - ALWAYS use current hostname to avoid stale IPs
        // Only use REACT_APP_EMULATOR_HOST if it matches current hostname (prevents using old cached IPs)
        const envHost = process.env.REACT_APP_EMULATOR_HOST;
        const emulatorHost = (envHost && envHost === hostname) ? envHost : hostname;
        console.log(`🌐 Detected network access from IP: ${hostname}`);
        console.log(`🌐 Using emulator host: ${emulatorHost}`);
        if (envHost && envHost !== hostname) {
            console.warn(`⚠️ REACT_APP_EMULATOR_HOST (${envHost}) differs from current hostname (${hostname})`);
            console.warn(`⚠️ Using current hostname (${hostname}) to prevent connection issues`);
        }
        return emulatorHost;
    }
    
    // Otherwise, accessing from localhost (desktop) - always use localhost
    // Even if REACT_APP_EMULATOR_HOST is set, ignore it when on localhost
    console.log(`💻 Using localhost for emulator connection`);
    return 'localhost';
};

const emulatorHost = getEmulatorHost();

// IMPORTANT: connectAuthEmulator MUST be called BEFORE any auth operations
// In development mode, always try to use emulators (unless explicitly disabled)
// Set REACT_APP_USE_EMULATORS=false to disable emulators and use production
const useEmulators = process.env.REACT_APP_USE_EMULATORS !== 'false' && process.env.NODE_ENV === 'development';

if (useEmulators) {
    try {
        // Connect to emulators synchronously (must be before any auth operations)
        // This will connect even if emulator isn't running - Firebase will handle the connection
        connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
        console.log(`🔧 Connected to Firebase Auth Emulator at ${emulatorHost}:9099`);
        
        connectFirestoreEmulator(db, emulatorHost, 8080);
        console.log(`🔧 Connected to Firestore Emulator at ${emulatorHost}:8080`);
        
        connectFunctionsEmulator(functions, emulatorHost, 5001);
        console.log(`🔧 Connected to Functions Emulator at ${emulatorHost}:5001`);
        
        connectStorageEmulator(storage, emulatorHost, 9199);
        console.log(`🔧 Connected to Storage Emulator at ${emulatorHost}:9199`);
        
        console.log('✅ All Firebase emulators connected successfully');
    } catch (error) {
        // If connection fails (e.g., already connected), log but don't throw
        // This can happen if the module is reloaded
        if (error.message && error.message.includes('already been called')) {
            console.log('ℹ️ Emulators already connected');
        } else {
            console.error('❌ Failed to connect to Firebase emulators:', error.message);
            console.error('❌ Make sure emulators are running: firebase emulators:start');
            console.error('❌ The app may not work correctly without emulators running');
        }
    }
} else {
    console.log('ℹ️ Using production Firebase (emulators disabled)');
}

// Set persistent auth state AFTER emulator connection
setPersistence(auth, browserLocalPersistence)
    .then(() => {
        console.log("Firebase persistence set to local");
    })
    .catch((error) => {
        console.error("Error setting persistence:", error);
    });
// Export auth and providers
export default auth;
export { 
    googleProvider,
    functions,
    storage,
    sendSignInLinkToEmail,
    signInWithEmailLink,
    app,
    analytics,
    auth,
    db,
    doc,
    getDoc
};